import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { formValueSelector } from 'redux-form';
import { breakpoints, Button, Card, Column, Columns, Stack } from '@etg/wings';
import { useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { LoadingDots, Notification, variants } from '@eti/components';
import { BoundNew, FareRule, PriceFormat } from '@eti/types';
import { Lock } from '@phosphor-icons/react';
import { useRef } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import CFCModal from '../../../common/cfc-modal/CFCModal';
import { PAYMENT } from '../../../constants/formNamesConstants';
import CartPriceSummary from '../../../widgets/cart/components/CartPriceSummary';
import SubUniqueSellingPoint from '../../../common/components/SubUniqueSellingPoint';
import { usePaymentMethod } from '../payment-methods/context/PaymentMethodsContext';
import { reduxFields } from '../constants/formFields';
import { paymentMethodCodes } from '../constants/paymentMethods';
import { hasRyanAirTicketingOrMarketingCarrier } from '../../../utils/carriersUtils';
import PayPalButtonPortalRoot from '../payment-methods/paypal/components/PayPalButtonPortalRoot';
import PaymentConditions from './payment-conditions/PaymentConditions';

const { PAYPAL, PAYPAL_PAY_LATER } = paymentMethodCodes;

const buttonStyles = (shouldHide) => css`
  display: ${shouldHide ? 'none' : 'flex'};
  min-height: auto;
  width: 100%;

  @media (min-width: ${breakpoints._768}) {
    margin: 0 auto;
    max-width: 22.5rem;
  }
`;

const loadingWrapperStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 auto;
  min-height: 49px;
  width: auto;

  @media (min-width: ${breakpoints._768}) {
    max-width: 22.5rem;
  }
`;

const dotsStyle = css`
  font-size: 1.4rem;
`;

const protectUSPStyles = css`
  align-items: center;
`;

const uspWrapperStyles = css`
  align-self: baseline;
`;

const payButtonWrapperStyles = css`
  align-self: end;
`;

const {
  INSTALMENT_FEE,
  PRICE_IN_CHARGE_CURRENCY,
  SELECTED_PAYMENT_METHOD,
  SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT,
  TOTAL_PRICE,
} = reduxFields;

const mapStateToProps = (state) => {
  const selectedPaymentMethod = formValueSelector(PAYMENT)(state, `${SELECTED_PAYMENT_METHOD}`);

  return {
    isPayPalSelected:
      selectedPaymentMethod?.code === PAYPAL || selectedPaymentMethod?.code === PAYPAL_PAY_LATER,
    paymentMethodCode: selectedPaymentMethod?.code,
    totalPrice: formValueSelector(PAYMENT)(state, TOTAL_PRICE),
    instalmentFee: formValueSelector(PAYMENT)(state, INSTALMENT_FEE),
    shouldAddInstallmentFeeInTotalAmount: formValueSelector(PAYMENT)(
      state,
      SHOULD_ADD_INSTALMENT_FEE_IN_TOTAL_AMOUNT,
    ),
    priceInChargeCurrency: formValueSelector(PAYMENT)(state, PRICE_IN_CHARGE_CURRENCY),
  };
};

const PaymentBottomContent = ({
  bounds,
  chargeCurrencyPriceFormat,
  discountAmount,
  fareRules,
  instalmentFee,
  isCFCModalOpen,
  isOtherChargeCurrency,
  isPayPalSelected,
  isPaying,
  isPaymentLinkCart = false,
  onSubmitWithNoPaymentMethod,
  paymentButtonText,
  paymentLinkCartPrice,
  paymentMethodCode,
  paymentMethodPrices,
  potentialChargeInDifferentCurrencyText,
  priceInChargeCurrency,
  selectedPaymentMethod,
  setIsCFCModalOpen,
  shouldAddInstallmentFeeInTotalAmount,
  shouldShowLoadingDots,
  subtotalPrice,
  totalPrice,
  handleSubmit,
}) => {
  const { onSubmit } = usePaymentMethod();
  const { p } = useProperty();
  const { t } = useTranslation();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();

  const paymentLinkInformation = t('PaymentLink.ReviewBox.Text');
  const isCFCEnabled = p('IbeClient.CarrierFlightConfirmation.Enabled');
  const isRyanAirTicketingOrMarketingCarrierPresent = hasRyanAirTicketingOrMarketingCarrier(
    p('IbeClient.RyanairMarketingCarriers'),
    bounds,
  );

  const hasCFCFlow = isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent;
  const shouldRenderPayPalButton = isPayPalSelected && !hasCFCFlow;

  const titles = {
    firstTitle: t('Payment.USPOne.Label'),
    secondTitle: t('Payment.USPTwo.Label'),
    thirdTitle: t('Payment.USPThree.Label'),
  };

  const cfcModalRef = useRef();

  const handleOpenCFCModal = () => {
    setIsCFCModalOpen(true);
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Payment',
        action: 'View',
        label: 'Pop up',
        property: 'Ryanair',
      });
    }
  };

  const handleCFCModalCompletePayment = () => {
    onSubmit();
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Payment',
        action: 'Click',
        label: 'Complete Payment',
        property: 'Ryanair',
      });
    }
  };

  const debouncedHandleCFCModalCompletePayment = useDebounceCallback(handleCFCModalCompletePayment);

  const handleCFCModalOnDismiss = () => {
    cfcModalRef?.current?.close();
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Payment',
        action: 'Click',
        label: 'Close pop up',
        property: 'Ryanair',
      });
    }
  };

  const trackClickOnPayButtonWithRyanair = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Payment',
        action: 'Click',
        label: 'Pay',
        property: 'Ryanair',
      });
    }
  };

  const handlePayButtonWithRyanair = () => {
    handleSubmit(() => {
      handleOpenCFCModal();
      trackClickOnPayButtonWithRyanair();
    })();
  };

  const payButtonOnClick =
    onSubmit && isCFCEnabled && isRyanAirTicketingOrMarketingCarrierPresent
      ? handlePayButtonWithRyanair
      : onSubmit || onSubmitWithNoPaymentMethod;

  const debouncedPayButtonOnClick = useDebounceCallback(payButtonOnClick ?? (() => {}));

  const uspWidths = {
    [breakpoints._0]: '4/4',
    [breakpoints._560]: '4/6',
    [breakpoints._768]: shouldRenderPayPalButton ? '2/4' : '4/6',
    [breakpoints._1440]: shouldRenderPayPalButton ? '7/12' : '4/6',
  };

  const payButtonWidths = {
    [breakpoints._0]: '4/4',
    [breakpoints._560]: shouldRenderPayPalButton ? '2/4' : '2/6',
    [breakpoints._1440]: shouldRenderPayPalButton ? '5/12' : '2/6',
  };

  return (
    <Card>
      <Card.Section>
        <Stack>
          <CartPriceSummary
            chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
            dataTestId="payment-bottomContent-priceSummary"
            discountAmount={discountAmount}
            instalmentFee={instalmentFee}
            isOtherChargeCurrency={isOtherChargeCurrency}
            isPaymentBottomContent
            paymentMethods={paymentMethodPrices}
            potentialChargeInDifferentCurrencyText={potentialChargeInDifferentCurrencyText}
            priceInChargeCurrency={priceInChargeCurrency}
            selectedPaymentMethod={selectedPaymentMethod}
            shouldAddInstallmentFeeInTotalAmount={shouldAddInstallmentFeeInTotalAmount}
            subTotalPrice={paymentLinkCartPrice || subtotalPrice}
            totalPrice={paymentLinkCartPrice || totalPrice}
          />
          {isPaymentLinkCart && paymentLinkInformation && (
            <Notification variant={variants.INFORMATION}>{paymentLinkInformation}</Notification>
          )}
          <PaymentConditions
            bounds={bounds}
            chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
            fareRules={fareRules}
            isOtherChargeCurrency={isOtherChargeCurrency}
            priceInChargeCurrency={priceInChargeCurrency}
          />
          {p('IbeClient.USP.Enabled') ? (
            <Columns
              className={protectUSPStyles}
              collapseBelow={shouldRenderPayPalButton ? breakpoints._768 : breakpoints._560}
            >
              <Column className={uspWrapperStyles} width={uspWidths}>
                <SubUniqueSellingPoint titles={titles} />
              </Column>
              <Column className={payButtonWrapperStyles} width={payButtonWidths}>
                <Button
                  className={buttonStyles(shouldRenderPayPalButton)}
                  data-testid="payment-pay-button"
                  isDisabled={isPaying}
                  onClick={debouncedPayButtonOnClick}
                  type="submit"
                  variant={variants.PRIMARY}
                >
                  {isPaying ? (
                    <LoadingDots />
                  ) : (
                    <>
                      {p('IbeClient.ButtonLockUSPPaymentMethods.Enabled') && (
                        <Lock color="currentcolor" data-testid="lock-icon" weight="fill" />
                      )}
                      <span>{paymentButtonText}</span>
                    </>
                  )}
                </Button>
                {!hasCFCFlow && (
                  <PayPalButtonPortalRoot
                    paymentMethodCode={paymentMethodCode}
                    shouldShow={shouldRenderPayPalButton && !shouldShowLoadingDots}
                  />
                )}
                {shouldShowLoadingDots && shouldRenderPayPalButton && (
                  <div className={loadingWrapperStyles}>
                    <LoadingDots dataTestId="loading-dots" dotsStyle={dotsStyle} />
                  </div>
                )}
              </Column>
            </Columns>
          ) : (
            <>
              <Button
                className={buttonStyles(shouldRenderPayPalButton)}
                data-testid="payment-pay-button"
                isDisabled={isPaying}
                onClick={debouncedPayButtonOnClick}
                type="submit"
                variant={variants.PRIMARY}
              >
                {isPaying ? (
                  <LoadingDots />
                ) : (
                  <>
                    {p('IbeClient.ButtonLockUSPPaymentMethods.Enabled') && (
                      <Lock color="#ffffff" data-testid="lock-icon" size={16} weight="fill" />
                    )}
                    <span>{paymentButtonText}</span>
                  </>
                )}
              </Button>
              {!hasCFCFlow && (
                <PayPalButtonPortalRoot
                  paymentMethodCode={paymentMethodCode}
                  shouldShow={shouldRenderPayPalButton && !shouldShowLoadingDots}
                />
              )}
              {shouldShowLoadingDots && shouldRenderPayPalButton && (
                <div className={loadingWrapperStyles}>
                  <LoadingDots dataTestId="loading-dots" dotsStyle={dotsStyle} />
                </div>
              )}
            </>
          )}
        </Stack>
      </Card.Section>
      {hasCFCFlow && (
        <CFCModal
          bottomContent={
            isPayPalSelected ? (
              <div>
                <PayPalButtonPortalRoot
                  paymentMethodCode={paymentMethodCode}
                  shouldShow={!shouldShowLoadingDots}
                />
                {shouldShowLoadingDots && (
                  <div className={loadingWrapperStyles}>
                    <LoadingDots dataTestId="loading-dots" dotsStyle={dotsStyle} />
                  </div>
                )}
              </div>
            ) : null
          }
          isOpen={isCFCModalOpen}
          onDismissModal={handleCFCModalOnDismiss}
          onProceedButtonClicked={debouncedHandleCFCModalCompletePayment}
          setIsOpen={setIsCFCModalOpen}
          shouldShowProceedButton
        />
      )}
    </Card>
  );
};

PaymentBottomContent.propTypes = {
  bounds: PropTypes.arrayOf(BoundNew),
  chargeCurrencyPriceFormat: PriceFormat,
  discountAmount: PropTypes.number,
  fareRules: PropTypes.arrayOf(FareRule),
  handleSubmit: PropTypes.func,
  instalmentFee: PropTypes.number,
  isCFCModalOpen: PropTypes.bool,
  isOtherChargeCurrency: PropTypes.bool,
  isPaying: PropTypes.bool,
  isPaymentLinkCart: PropTypes.bool,
  isPayPalSelected: PropTypes.bool,
  onSubmitWithNoPaymentMethod: PropTypes.func,
  paymentButtonText: PropTypes.string,
  paymentLinkCartPrice: PropTypes.number,
  paymentMethodCode: PropTypes.string,
  paymentMethodPrices: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      price: PropTypes.number,
      serviceFee: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
  potentialChargeInDifferentCurrencyText: PropTypes.string,
  priceInChargeCurrency: PropTypes.number,
  selectedPaymentMethod: PropTypes.shape({
    label: PropTypes.string,
    price: PropTypes.number,
    serviceFee: PropTypes.number,
  }),
  setIsCFCModalOpen: PropTypes.func,
  shouldAddInstallmentFeeInTotalAmount: PropTypes.bool,
  shouldShowLoadingDots: PropTypes.bool,
  subtotalPrice: PropTypes.number,
  totalPrice: PropTypes.number,
};

export default connect(mapStateToProps)(PaymentBottomContent);
